import { ComponentProps, useState } from "react";
import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { ContentCardScaffold } from "../contents/cards/scaffolds/ContentCardScaffold";
import { ArchiveTags } from "./ArchiveTags";
import { ArchiveIconButton } from "lib/icons";
import { Bookmark } from "components/core/inputs/Bookmark";
import { Download } from "components/core/inputs/Download";
import { getGoogleDriveThumbnailUrl } from "utils/googleDrive";
import Image from "next/image";
import { useArchiveBookmark } from "hooks/useArchiveBookmark";
import { useAuth } from "hooks/useAuth";

type ArchiveItem = {
  id: string;
  title: string;
  source: string;
  sourcename: string;
  mix_archive_sourcename?: {
    imgurl?: string | null;
    source: string;
    sourcename: string;
  } | null;
  view_url: string;
  download_url: string;
  thumbnail_url?: string | null;
  tags?: Array<string> | null;
  created_at?: any | null;
  updated_at?: any | null;
  bookmarked?: boolean;
};

const ThumbnailBox = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  background: var(--color-grey-100);
`;


const IconButtonContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  display: flex;
  gap: 8px;
  animation: slideInFromRight 0.3s ease-out forwards;

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: -32px;
  left: 50%;
  transform: translateX(-85%);
  background: white;
  color: black;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;

  &::before {
    content: "";
    position: absolute;
    top: -4px;
    right: 10px;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid white;
  }
`;

const IconButtonWrapper = styled.div`
  position: relative;
  animation: buttonSlideIn 0.25s ease-out forwards;
  opacity: 0;

  &:nth-of-type(1) {
    animation-delay: 0.05s;
  }

  &:nth-of-type(2) {
    animation-delay: 0.15s;
  }

  @keyframes buttonSlideIn {
    from {
      opacity: 0;
      transform: translateX(15px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &:hover ${Tooltip} {
    opacity: 1;
  }
`;

const SourceInfo = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SourceLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
`;

// Calculate time difference for "X일전" display
const getTimeAgo = (date: any) => {
  if (!date) return "";
  const now = new Date();
  const past = new Date(date);
  const diffInDays = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "오늘";
  if (diffInDays === 1) return "1일전";
  return `${diffInDays}일전`;
};

export const ArchiveVerticalCard = ({
  archive,
  tagEnums,
  onClick,
  onDownload,
  isGuest = false,
  ...props
}: Omit<ComponentProps<typeof ContentCardScaffold>, "children"> & {
  archive: ArchiveItem;
  tagEnums: Array<{ value: string; comment?: string | null }>;
  onClick?: () => void;
  onDownload?: (e: React.MouseEvent) => void;
  isGuest?: boolean;
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloadClicked, setIsDownloadClicked] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const { toggle: toggleBookmark } = useArchiveBookmark(archive);
  const { openSignInModal } = useAuth();

  // Generate thumbnail from view_url if thumbnail_url is not available
  const thumbnailUrl =
    archive.thumbnail_url ||
    (archive.view_url ? getGoogleDriveThumbnailUrl(archive.view_url, "w1920") : null);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isGuest) {
      openSignInModal();
      return;
    }

    // Show orange color momentarily
    setIsDownloadClicked(true);
    setTimeout(() => {
      setIsDownloadClicked(false);
    }, 200);

    if (onDownload) {
      onDownload(e);
    } else {
      window.open(archive.download_url, "_blank");
    }
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBookmark();
  };

  return (
    <ContentCardScaffold
      css={css`
    `}
      {...props}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ThumbnailBox>
        <ContentCardScaffold.ThumbnailImg
          css={css`
            border-radius: inherit;
          `}
          src={thumbnailUrl ?? ""}
          unoptimized
          fill={true}
          onFallbackChange={setShowFallback}
        />
        {!isHovered ? (
            <ArchiveIconButton css={css`
              position: absolute;
              top: 10px;
              width: 32px;
              height: 32px;
              right: 10px;
              z-index: 1;
            `} />
          ) : (
            <IconButtonContainer>
              <IconButtonWrapper>
                <div
                  onClick={handleDownload}
                  css={css`
                    cursor: pointer;
                  `}
                >
                  <Download
                    variant="filled"
                    checked={isDownloadClicked}
                    onChange={() => { }}
                  />
                </div>
                <Tooltip>다운로드</Tooltip>
              </IconButtonWrapper>
              <IconButtonWrapper>
                <div
                  onClick={handleBookmark}
                  css={css`
                    cursor: pointer;
                  `}
                >
                  <Bookmark
                    variant="filled"
                    checked={archive.bookmarked || false}
                    onChange={() => { }}
                  />
                </div>
              </IconButtonWrapper>
            </IconButtonContainer>
          )}
        </ThumbnailBox>

      {archive.sourcename && (
        <SourceInfo>
          <SourceLogo
            src={archive.mix_archive_sourcename?.imgurl || "https://cdn.mix.day/img/mix.png"}
            alt={archive.sourcename}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex: 1 1 0%;
          `}>
            <div
              css={css`
              font-size: 13px;
              font-weight: 400;
              line-height: 140%;
            `}
            >
              {archive.sourcename}
            </div>

            {archive.created_at && (
              <div
                css={css`
                  font-size: 12px;
                  color: var(--color-text-secondary);
                `}
              >
                {getTimeAgo(archive.created_at)}
              </div>
            )}
          </div>
        </SourceInfo>
      )}

      <ContentCardScaffold.Title
        css={css`
          font-size: 16px;
          font-weight: 600;
          line-height: 140%;
          word-break: keep-all;
          margin-top: 12px;
          margin-bottom: 16px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        `}
      >
        {archive.title}
      </ContentCardScaffold.Title>

      {archive.tags && archive.tags.length > 0 && (
        <ArchiveTags tags={archive.tags} tagEnums={tagEnums} />
      )}
    </ContentCardScaffold>
  );
};
